import { QueryClient, type QueryKey, type UseSuspenseQueryResult, useSuspenseQuery } from "@tanstack/react-query"
import { AxiosError } from "axios"

import { minutes, seconds } from "@/3514/utils"

import { ECoachCalendarQueryKey as EQueryKey } from "./coach.calendar.api.config"
import adapters from "./coach.calendar.api.queries.adapters"
import fetchers from "./coach.calendar.api.queries.fetchers"
import type {
    ICoachCalendarMsTeamsAccountDto as IMsTeamsAccountDto,
    ICoachCalendarMsTeamsAccountModel as IMsTeamsAccountModel,
    ICoachCalendarRelinkAccountsQueryDto as IRelinkAccountsQueryDto,
    ICoachCalendarRelinkAccountsQueryModel as IRelinkAccountsQueryModel,
    ICoachCalendarZoomAccountDto as IZoomAccountDto,
    ICoachCalendarZoomAccountModel as IZoomAccountModel
} from "./coach.calendar.api.types"

type TZoomAccountQuery = UseSuspenseQueryResult<IZoomAccountModel, AxiosError>
type TMsTeamsAccountQuery = UseSuspenseQueryResult<IMsTeamsAccountModel, AxiosError>

function useZoom(): ReturnType<() => TZoomAccountQuery> {
    return useSuspenseQuery<IZoomAccountDto, AxiosError, IZoomAccountModel>({
        queryKey: EQueryKey.ZoomAccount(),
        queryFn: fetchers.zoomAccount,
        select: adapters.zoomAccount,
        staleTime: minutes(10),
        gcTime: minutes(5),
        retry: false,
        refetchOnWindowFocus: "always",
        refetchOnMount: true,
        networkMode: "offlineFirst",
        notifyOnChangeProps: ["data"]
    })
}

function useMsTeams(): ReturnType<() => TMsTeamsAccountQuery> {
    return useSuspenseQuery<IMsTeamsAccountDto, AxiosError, IMsTeamsAccountModel>({
        queryKey: EQueryKey.MsTeamsAccount(),
        queryFn: fetchers.msTeamsAccount,
        select: adapters.msTeamsAccount,
        staleTime: minutes(10),
        gcTime: minutes(5),
        retry: false,
        refetchOnWindowFocus: "always",
        refetchOnMount: true,
        networkMode: "offlineFirst",
        notifyOnChangeProps: ["data"]
    })
}

function useAccounts(): ReturnType<() => { zoom: TZoomAccountQuery; msTeams: TMsTeamsAccountQuery }> {
    const zoom: TZoomAccountQuery = useZoom()
    const msTeams: TMsTeamsAccountQuery = useMsTeams()

    return { zoom, msTeams }
}

async function coachCalendarAccountQueriesPrefetcher({ qc }: { qc: QueryClient }): Promise<void> {
    await qc.prefetchQuery<IZoomAccountDto, AxiosError, IZoomAccountModel>({
        queryKey: EQueryKey.ZoomAccount(),
        queryFn: fetchers.zoomAccount,
        staleTime: minutes(10),
        gcTime: minutes(5),
        retry: false
    })
    await qc.prefetchQuery<IMsTeamsAccountDto, AxiosError, IMsTeamsAccountModel>({
        queryKey: EQueryKey.MsTeamsAccount(),
        queryFn: fetchers.msTeamsAccount,
        staleTime: minutes(10),
        gcTime: minutes(5),
        retry: false
    })
}

type TRelinkAccountsQuery = UseSuspenseQueryResult<IRelinkAccountsQueryModel, AxiosError>

function useRelinkAccounts(): ReturnType<() => TRelinkAccountsQuery> {
    return useSuspenseQuery<IRelinkAccountsQueryDto, AxiosError, IRelinkAccountsQueryModel, QueryKey>({
        queryKey: EQueryKey.RelinkAccounts(),
        queryFn: fetchers.relinkAccounts,
        select: adapters.relinkAccounts,
        staleTime: minutes(30),
        gcTime: minutes(15),
        refetchOnWindowFocus: "always",
        refetchOnMount: true,
        retry: 1,
        retryDelay: seconds(5),
        networkMode: "offlineFirst",
        notifyOnChangeProps: ["data", "isRefetching", "refetch"]
    })
}

async function coachCalendarRelinkAccountsQueryPrefetcher({ qc }: { qc: QueryClient }): Promise<void> {
    await qc.prefetchQuery<IRelinkAccountsQueryDto, AxiosError, IRelinkAccountsQueryModel, QueryKey>({
        queryKey: EQueryKey.RelinkAccounts(),
        queryFn: fetchers.relinkAccounts,
        staleTime: minutes(30),
        gcTime: minutes(15),
        retry: false
    })
}

export { useAccounts as useCoachCalendarAccountsQuery, useRelinkAccounts as useCoachCalendarRelinkAccountsQuery }
export { coachCalendarAccountQueriesPrefetcher, coachCalendarRelinkAccountsQueryPrefetcher }
